import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX, Instagram, Maximize2 } from "lucide-react";

export default function ReelCard({ reel, index }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: [0, 0.5, 1] }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const goFullscreen = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      data-testid={`reel-video-${reel.id}`}
      className="group"
    >
      <div
        onClick={togglePlay}
        className="relative aspect-[9/16] overflow-hidden bg-foreground cursor-pointer border border-background/10"
      >
        <video
          ref={videoRef}
          src={reel.src}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Top overlay */}
        <div className="absolute top-3 right-3 flex items-center justify-end pointer-events-none">
          <button
            type="button"
            onClick={goFullscreen}
            aria-label="Fullscreen"
            data-testid={`reel-fullscreen-${reel.id}`}
            className="pointer-events-auto bg-background/80 backdrop-blur-sm p-1.5 hover:bg-primary hover:text-primary-foreground transition-colors text-foreground"
          >
            <Maximize2 size={12} />
          </button>
        </div>

        {/* Center play state when paused */}
        {!playing && (
          <div className="absolute inset-0 flex items-center justify-center bg-foreground/40">
            <div className="bg-primary text-primary-foreground p-5 group-hover:scale-110 transition-transform">
              <Play size={28} fill="currentColor" />
            </div>
          </div>
        )}

        {/* Bottom mute button only */}
        <div className="absolute bottom-3 right-3">
          <button
            type="button"
            onClick={toggleMute}
            aria-label={muted ? "Unmute" : "Mute"}
            data-testid={`reel-mute-${reel.id}`}
            className="bg-background/80 backdrop-blur-sm p-2 hover:bg-primary hover:text-primary-foreground transition-colors text-foreground"
          >
            {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
